import React from 'react';

import { useIntl } from 'react-intl';

import { useCreateGateway } from 'src/features/gateway/api/useCreateGateway';
import { LIST_GATEWAYS_RESOURCE } from 'src/features/gateway/api/useListGateways';
import GatewayFormModal from 'src/features/gateway/components/form/GatewayFormModal';
import SettingsContext from 'src/features/administration/context/SettingsContext';
import { Gateway } from 'src/features/gateway/utils/types';
import { gatewayMessages } from 'src/features/gateway/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';

interface GatewayFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedGateway?: Gateway;
    children?: React.ReactNode;
}

function CreateGatewayFormModal<TData>({ className, semantics }: GatewayFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const settingsData = React.useContext(SettingsContext);

    const { mutate, isPending } = useCreateGateway({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_GATEWAYS_RESOURCE] });
            toast({
                title: formatMessage(gatewayMessages.createGatewaySuccess),
                description: formatMessage(gatewayMessages.createGatewaySuccessDescription, {
                    entry: success.translations.find(
                        (translation) => translation.language === settingsData?.default_language,
                    )?.name,
                }),
            });
        },
    });

    return (
        <GatewayFormModal
            className={className}
            mutate={(values, onSuccess, onError) => mutate({ ...values, parkingId }, { onSuccess, onError })}
            title={formatMessage(generalMessages.createEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.ADD}
            isPending={isPending}
            subtitle={formatMessage(gatewayMessages.addGatewaySubtitle)}
        />
    );
}

export default CreateGatewayFormModal;
